import { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, Shield, Users, Server, Heart, FileText, Sparkles, MessageCircle, Zap, Trophy, Gamepad2, Briefcase, Fish, Swords } from 'lucide-react';

import { BOT_CONFIG } from '../config/bot';
import BotAvatar from '../components/BotAvatar';
import SlidingFeatures from '../components/SlidingFeatures';
import DiscordPreviewCard from '../components/DiscordPreviewCard';
import { fetchBotStats } from '../services/botStatsService';
import imageConfig from '../config/images.json';

const getEmojiUrl = (emojiId: string, size: number = 16): string =>
  `https://cdn.discordapp.com/emojis/${emojiId}.webp?size=${size}&quality=lossless`;

const generateProgressBar = (current: number, max: number, length: number = 10): string => {
  const progress = Math.min(Math.max(current / max, 0), 1);
  const filledSegments = Math.round(progress * length);
  const percentage = Math.round(progress * 100);
  const emojiIds = {
    front_empty: '1421229056946470993', front_full: '1421224992263114905',
    mid_empty: '1421229540444737727', mid_full: '1421222542596771891',
    back_empty: '1421228774761959464', back_full: '1421225912657252452',
  };
  const img = (id: string) => `<img src="${getEmojiUrl(id)}" style="width:20px;height:20px;display:inline-block;vertical-align:middle;" />`;
  let bar = `<span style="color:#dbdee1;font-family:monospace;">${current}/${max}</span> `;
  bar += img(filledSegments > 0 ? emojiIds.front_full : emojiIds.front_empty);
  for (let i = 1; i < length - 1; i++) bar += img(i < filledSegments ? emojiIds.mid_full : emojiIds.mid_empty);
  bar += img(emojiIds.back_empty);
  bar += ` <span style="color:#dbdee1;font-family:monospace;vertical-align:middle;">${percentage}%</span>`;
  return bar;
};
void generateProgressBar; // Used dynamically in quest showcase data

/* ─── Scroll-reveal wrapper ─── */
const Reveal = ({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 36 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* ─── Animated counter ─── */
const Counter = ({ value, suffix = '' }: { value: number; suffix?: string }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (!inView) return;
    let frame: number;
    const start = performance.now();
    const animate = (t: number) => {
      const p = Math.min((t - start) / 1400, 1);
      setDisplay(Math.floor(p * value));
      if (p < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [inView, value]);

  return (
    <span ref={ref}>
      {display}<span className="text-primary">{suffix}</span>
    </span>
  );
};

const parseStatValue = (raw: number | string) => {
  if (typeof raw === 'number') return { value: raw, suffix: '' };
  const match = raw.match(/([0-9.]+)/);
  const value = match ? parseFloat(match[1]) : 0;
  const suffix = raw.replace(match ? match[1] : '', '').trim();
  return { value, suffix };
};

/* ─── Feature data (sourced exactly from bot cogs & config JSONs) ─── */
const featureShowcases = [
  /* ── 1. Gacha Draw System ── */
  {
    id: 'draw',
    icon: Swords,
    title: 'Anime Card Gacha',
    description: 'Pull 3 anime character cards per draw. Five rarity tiers — Bronze, Silver, Gold, Platinum, Obsidian — each with metallic card frames. Claim favorites, build your collection, trade with friends, and steal from rivals.',
    tags: ['5 Rarity Tiers', 'Trading System', 'Collection Gallery', 'Wishlist', 'Steal System'],
    preview: {
      command: '.draw',
      image: '/images/showcase/draw-cards.png',
      embedColor: '#FF6B9D',
      footer: '💰 Cost: 50 pts per pull • 3 cards each draw',
    },
  },
  /* ── 2. Multiplayer Wordle ── */
  {
    id: 'wordle',
    icon: Gamepad2,
    title: 'Multiplayer Wordle',
    description: 'Host a Wordle game right in your channel. Friends join via button, everyone plays in DMs simultaneously. Guess the 5-letter word in 6 attempts — winner takes 10% of the host\'s balance plus 250 stella points.',
    tags: ['Multiplayer', 'Play in DMs', 'Stella Rewards', 'Live Leaderboard'],
    preview: {
      command: '.wordle',
      image: '/images/showcase/wordle-lobby.png',
      embedColor: '#538d4e',
      footer: 'Game starts in 30s • Solo or multiplayer!',
    },
  },
  /* ── 3. 30+ Action Commands ── */
  {
    id: 'actions',
    icon: Heart,
    title: '30+ Social Actions',
    description: 'Hug, pat, bite, bonk, dance, and 25 more — each with anime GIFs, personality-driven messages, and a "Do It Back" button. Every interaction is tracked with sent and received counters.',
    tags: ['Anime GIFs', 'Do It Back', 'Interaction Tracking', '30+ Actions'],
    preview: {
      command: '.hug @Senko',
      title: 'Yor hugs Senko',
      embedColor: '#FF6B9D',
      hasGif: true,
      gifUrl: 'https://media1.tenor.com/m/sQ_isTxT-EEAAAAd/anya-hug.gif',
      footer: 'Sent: 42 | Received: 38',
    },
  },
  /* ── 4. Spy×Family Economy ── */
  {
    id: 'economy',
    icon: Briefcase,
    title: 'Spy×Family Economy',
    description: 'Work jobs themed around your chosen Spy×Family character — Loid earns 1.5× as a Psychiatrist, Yor earns 1.3× as a City Hall Clerk, and Anya studies at Eden Academy. Rob other players, take spy missions, and gamble your earnings.',
    tags: ['Character Jobs', 'Rob System', 'Spy Missions', 'Gambling'],
    preview: {
      command: '.work',
      title: '💼 Psychiatrist',
      description: 'You helped patients at the hospital.',
      embedColor: '#57F287',
      fields: [
        { name: '💰 Earned', value: '**+52** pts', inline: true },
        { name: '💳 Balance', value: '**1,247** pts', inline: true },
        { name: '🌟 Loid Forger Bonus', value: '+50%', inline: true },
      ],
    },
  },
  /* ── 5. Fishing System ── */
  {
    id: 'fishing',
    icon: Fish,
    title: 'Deep-Sea Fishing',
    description: '27 species across 6 rarities — from Anchovy to Kraken. Upgrade your rod, buy specialized bait, and complete your collection. Legendary catches like the Dragon Fish are worth up to 2,000 stella points.',
    tags: ['27 Species', 'Rod Upgrades', 'Bait System', 'Collection'],
    preview: {
      command: '.fish',
      title: '🎣 Fishing Adventure',
      description: 'Cast your line and catch fish to complete your collection!',
      embedColor: '#3498db',
      fields: [
        { name: '🎣 Equipment', value: '**Rod:** 🎣 Silver Rod\n**Durability:** 🟢 120/150\n**Hook:** 🪝 Barbed Hook\n**Bait:** 🪱 8 remaining', inline: true },
        { name: '📊 Your Stats', value: '**Total Caught:** 47 🐟\n**Species Found:** 12/27\n**Collection:** 44.4% complete', inline: true },
      ],
    },
  },
  /* ── 6. Moderation Toolkit ── */
  {
    id: 'moderation',
    icon: Shield,
    title: 'Full Moderation Suite',
    description: 'Kick, ban, warn, timeout, mute — all with automatic case tracking, DM notifications to offenders, and a dedicated mod log channel. Manage roles in bulk, lock channels during raids, and review full case history.',
    tags: ['Case System', 'Mod Logs', 'Role Management', 'Auto DM'],
    preview: {
      command: '.warn @TroubleMaker Spamming in #general',
      title: 'Warning',
      embedColor: '#FEE75C',
      fields: [
        { name: 'User', value: '@TroubleMaker (`918273645...`)', inline: true },
        { name: 'Moderator', value: '@Senko', inline: true },
        { name: 'Reason', value: 'Spamming in #general', inline: false },
        { name: 'Total Warnings', value: '2', inline: true },
      ],
      footer: 'Case #15',
    },
  },
  /* ── 7. Quest System ── */
  {
    id: 'quests',
    icon: Trophy,
    title: 'Quest System',
    description: 'Complete quests by talking, reacting, and engaging naturally. Members earn stella points for being active — no grinding required, just real conversations that keep your server alive.',
    tags: ['Daily Quests', 'Message Rewards', 'Reaction Tracking', 'Leaderboards'],
    preview: {
      command: '.quest',
      title: 'Your Quests',
      embedColor: '#FF6B9D',
      fields: [
        { name: '', value: '**#1** - Send: Hello {user}\n`0/5` ' + Array(9).fill(0).map((_, i) => `<img src="${getEmojiUrl(i === 0 ? '1421229056946470993' : i === 8 ? '1421228774761959464' : '1421229540444737727')}" style="width:20px;height:20px;display:inline-block;vertical-align:middle;" />`).join('') + ' `0%`\n📍 In this channel • `100 stp`', inline: false },
        { name: '', value: '**#2** - React with: :thumbsup:\n`2/10` ' + Array(9).fill(0).map((_, i) => `<img src="${getEmojiUrl(i < 3 ? (i === 0 ? '1421224992263114905' : '1421222542596771891') : i === 8 ? '1421228774761959464' : '1421229540444737727')}" style="width:20px;height:20px;display:inline-block;vertical-align:middle;" />`).join('') + ' `20%`\n📍 In this channel • `250 stp`', inline: false },
      ],
      footer: "User's quests • Page 1/1",
    },
  },
];

const HomePage = () => {
  const [stats, setStats] = useState({
    servers: { value: 0, suffix: '' },
    users: { value: 0, suffix: '' },
    commands: { value: 0, suffix: '' },
  });

  useEffect(() => {
    fetchBotStats().then(s => setStats({
      servers: parseStatValue(s.servers),
      users: parseStatValue(s.users),
      commands: parseStatValue(s.commands),
    }));
  }, []);

  const heroImage = imageConfig.hero.url || null;
  const featureImage = imageConfig.feature.url || null;

  return (
    <div className="relative overflow-x-hidden">

      {/* ════════════════════════  HERO  ════════════════════════ */}
      <section className="relative flex flex-col justify-center pt-32 pb-16 lg:pt-40 lg:pb-24 overflow-hidden">
        {/* Mesh background */}
        <div className="absolute inset-0 mesh-gradient pointer-events-none" />
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-primary/[0.07] blur-[120px] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

            {/* Left — copy */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="text-center lg:text-left"
            >
              <div className="inline-flex items-center gap-2 px-4 py-1.5 glass rounded-full mb-6">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-xs text-gray-300 font-medium tracking-wide">Online&nbsp;&nbsp;•&nbsp;&nbsp;v{BOT_CONFIG.version}</span>
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-[4.25rem] font-display font-bold leading-[1.08] mb-6">
                <span className="text-white">Your server</span>
                <br />
                <span className="text-white">deserves </span>
                <span className="text-gradient">{BOT_CONFIG.name}</span>
              </h1>

              <p className="text-lg sm:text-xl text-gray-400 leading-relaxed max-w-xl mx-auto lg:mx-0 mb-8">
                Anime gacha cards, multiplayer Wordle, a full fishing system, 30+ social actions with GIFs, 
                Spy&times;Family economy, quest-driven engagement, and a complete moderation toolkit — 
                all in <span className="text-primary">one bot</span>.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <a
                  href={BOT_CONFIG.inviteLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="
                    group glow-button inline-flex items-center justify-center gap-2.5
                    px-8 py-4 bg-gradient-primary text-white font-semibold rounded-2xl
                    hover:shadow-lg hover:shadow-primary/30 hover:-translate-y-0.5
                    active:scale-[0.97] transition-all duration-300 text-[15px]
                  "
                >
                  <Sparkles className="w-[18px] h-[18px]" />
                  Add to Discord
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <a
                  href={BOT_CONFIG.supportServer}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="
                    inline-flex items-center justify-center gap-2.5
                    px-8 py-4 glass hover:bg-white/[0.06] text-white font-semibold rounded-2xl
                    hover:-translate-y-0.5 active:scale-[0.97]
                    transition-all duration-300 text-[15px]
                  "
                >
                  <Users className="w-[18px] h-[18px]" />
                  Join Community
                </a>
              </div>
            </motion.div>

            {/* Right — hero visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="flex justify-center lg:justify-end relative"
            >
              {heroImage ? (
                <div className="relative">
                  <div className="absolute inset-0 bg-primary/20 rounded-full blur-[80px] scale-75" />
                  <img
                    src={heroImage}
                    alt="Anya"
                    className="relative w-72 sm:w-80 md:w-96 lg:w-[420px] drop-shadow-2xl select-none"
                    draggable={false}
                  />
                </div>
              ) : (
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/25 to-secondary/15 rounded-full blur-[60px] scale-110 animate-pulse" />
                  <div className="relative w-56 h-56 sm:w-72 sm:h-72 md:w-80 md:h-80 lg:w-96 lg:h-96 rounded-full overflow-hidden ring-2 ring-white/[0.06] shadow-2xl shadow-primary/10">
                    <BotAvatar className="w-full h-full object-cover" size={512} />
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════════════════════  STATS BAR SECTION  ════════════════════════ */}
      <section className="py-12 sm:py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/[0.02] to-transparent pointer-events-none" />
        <Reveal className="max-w-4xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="glass-card px-6 sm:px-12 py-8 sm:py-10 border border-white/10 shadow-2xl backdrop-blur-xl bg-white/[0.02] hover:border-primary/30 transition-all duration-500 rounded-3xl">
            <div className="grid grid-cols-3 gap-4 sm:gap-6 text-center divide-x divide-white/[0.08]">
              {[
                { icon: Server, label: 'Servers', ...stats.servers },
                { icon: Users, label: 'Users', ...stats.users },
                { icon: FileText, label: 'Commands', ...stats.commands },
              ].map((s, idx) => (
                <div key={s.label} className={`space-y-2 group ${idx !== 0 ? 'pl-3 sm:pl-6' : ''}`}>
                  <div className="w-11 h-11 mx-auto rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-3 group-hover:scale-110 group-hover:bg-primary/20 group-hover:border-primary/40 transition-all duration-300 shadow-sm shadow-primary/10">
                    <s.icon className="w-5.5 h-5.5 text-primary" />
                  </div>
                  <div className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
                    <Counter value={s.value} suffix={s.suffix} />
                  </div>
                  <div className="text-[11px] sm:text-xs uppercase tracking-[0.18em] text-gray-400 font-semibold">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* ════════════════════════  PURPOSE  ════════════════════════ */}
      <section className="py-20 sm:py-28 relative">
        <Reveal className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[11px] sm:text-xs uppercase tracking-[0.3em] text-primary/70 mb-4 font-medium">Why Anya exists</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white mb-6 leading-tight">
            Not another generic bot
          </h2>
          <p className="text-base sm:text-lg text-gray-400 leading-relaxed max-w-2xl mx-auto">
            Dead servers don't need more slash commands. They need <em className="text-gray-200 not-italic font-medium">reasons to come back</em> — 
            quests that reward real conversations, a gacha system worth grinding, multiplayer games 
            that pull people into the same channel, and an economy tied to Spy×Family lore. 
            Anya is built to make your community <span className="text-gradient">want to stay</span>.
          </p>
        </Reveal>
      </section>

      {/* ════════════════════════  FEATURES  ════════════════════════ */}
      {featureShowcases.map((feature, idx) => {
        const flipped = idx % 2 === 1;
        const showAnyaImage = feature.id === 'economy' && featureImage;

        return (
          <section key={feature.id} className="py-20 sm:py-28 relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none">
              <div className={`absolute ${flipped ? 'left-0' : 'right-0'} top-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-primary/[0.04] rounded-full blur-[120px]`} />
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
              <div className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${flipped ? 'lg:[direction:rtl]' : ''}`}>
                {/* Preview side */}
                <Reveal className="lg:[direction:ltr]">
                  <div className="relative">
                    <div className="absolute -inset-6 bg-gradient-to-br from-primary/8 to-transparent rounded-3xl blur-2xl" />
                    <DiscordPreviewCard
                      {...feature.preview}
                      className="relative shadow-2xl hover:shadow-primary/10 hover:scale-[1.015] transition-all duration-500"
                    />
                    {showAnyaImage && (
                      <img
                        src={featureImage}
                        alt="Anya pointing"
                        className="absolute -left-16 -bottom-12 w-36 sm:w-44 drop-shadow-xl select-none hidden lg:block"
                        draggable={false}
                      />
                    )}
                  </div>
                </Reveal>

                {/* Content side */}
                <Reveal delay={0.12} className="lg:[direction:ltr] space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-2xl bg-primary/10 text-primary shrink-0">
                      <feature.icon className="w-7 h-7" />
                    </div>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold text-white">
                      {feature.title}
                    </h2>
                  </div>

                  <p className="text-gray-400 text-base sm:text-lg leading-relaxed">
                    {feature.description}
                  </p>

                  <div className="flex flex-wrap gap-2">
                    {feature.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3.5 py-1.5 rounded-full text-xs font-medium glass text-gray-300 hover:text-primary hover:border-primary/20 transition-colors"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="pt-2">
                    <span className="text-[11px] text-gray-500 uppercase tracking-[0.15em]">Try it</span>
                    <code className="ml-3 px-3 py-1.5 glass rounded-lg text-primary text-sm font-mono">
                      {feature.preview.command}
                    </code>
                  </div>
                </Reveal>
              </div>
            </div>
          </section>
        );
      })}

      {/* ════════════════════════  LIVE CAROUSEL  ════════════════════════ */}
      <section className="py-20 sm:py-28 relative">
        <div className="absolute inset-0 bg-white/[0.01] pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Reveal className="text-center mb-14">
            <p className="text-[11px] sm:text-xs uppercase tracking-[0.3em] text-primary/70 mb-3 font-medium">Live Preview</p>
            <h3 className="text-2xl sm:text-3xl font-display font-bold text-white">See it in action</h3>
          </Reveal>
          <SlidingFeatures />
        </div>
      </section>

      {/* ════════════════════════  FINAL CTA  ════════════════════════ */}
      <section className="py-24 sm:py-32 relative">
        <div className="absolute inset-0 mesh-gradient opacity-50 pointer-events-none" />
        <Reveal className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <div className="inline-block mb-8 relative group">
            <div className="absolute inset-0 bg-primary/20 rounded-full blur-2xl scale-125 group-hover:scale-150 transition-transform duration-700" />
            <div className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden ring-2 ring-white/[0.08] shadow-xl">
              <BotAvatar className="w-full h-full object-cover" size={128} />
            </div>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white mb-4 leading-tight">
            Your server is incomplete without this
          </h2>

          <p className="text-lg text-gray-400 mb-10 max-w-lg mx-auto leading-relaxed">
            Gacha cards, multiplayer games, 30+ actions, a full economy, fishing, moderation, 
            and quest-driven engagement — <span className="text-primary">zero setup required</span>.
          </p>

          <a
            href={BOT_CONFIG.inviteLink}
            target="_blank"
            rel="noopener noreferrer"
            className="
              group glow-button inline-flex items-center justify-center gap-3
              px-10 py-5 bg-gradient-primary text-white font-semibold rounded-2xl text-base
              hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-1
              active:scale-[0.97] transition-all duration-500
            "
          >
            <Sparkles className="w-5 h-5" />
            Add to Discord
            <ArrowRight className="w-[18px] h-[18px] group-hover:translate-x-1.5 transition-transform duration-300" />
          </a>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-8 text-gray-500 text-xs sm:text-sm">
            {[
              { icon: MessageCircle, text: 'Community Focused' },
              { icon: Zap, text: 'Always Improving' },
              { icon: Heart, text: 'Made With Love' },
            ].map((t) => (
              <span key={t.text} className="flex items-center gap-2">
                <t.icon className="w-3.5 h-3.5 text-primary/40" />
                {t.text}
              </span>
            ))}
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default HomePage;
